import React, { useState, useRef, useEffect } from 'react'
import { ArrowLeftRight } from 'lucide-react'
import { useLanguage } from '../context/LanguageContext'

const getBeforeAfterData = (language) => [
  {
    id: 'erhverv',
    title: language === 'da' ? 'Erhvervsrengøring' : 'Commercial Cleaning',
    description: language === 'da' 
      ? 'Effektiv og grundig aftørring, støvsugning og desinfektion af kontorarbejdspladser.'
      : 'Efficient and thorough wiping, vacuuming, and disinfection of office workplaces.',
    before: 'https://assets.auxodev.dk/assets/b84a6f1a-652f-4145-af52-d7d46fab18e5/98f1d15c-761e-4644-be4f-f6341a7c6972.jpg',
    after: 'https://assets.auxodev.dk/assets/b84a6f1a-652f-4145-af52-d7d46fab18e5/d69249ac-de35-4be3-a7cf-4c40473bcaec.jpg',
    beforeLabel: language === 'da' ? 'Før rengøring' : 'Before cleaning',
    afterLabel: language === 'da' ? 'Efter rengøring' : 'After cleaning'
  },
  {
    id: 'byggeplads',
    title: language === 'da' ? 'Byggepladsservice' : 'Construction Site Service',
    description: language === 'da'
      ? 'Fjernelse af groft byggeaffald, gipsstøv, malingsrester og klargøring til aflevering.'
      : 'Removal of coarse construction waste, plaster dust, paint residue, and preparation for handover.',
    before: 'https://assets.auxodev.dk/assets/b84a6f1a-652f-4145-af52-d7d46fab18e5/643b2df7-8b07-4c7f-88ff-a9291a21f3f8.jpg',
    after: 'https://assets.auxodev.dk/assets/b84a6f1a-652f-4145-af52-d7d46fab18e5/79676e21-d94f-43f3-a75b-b0be6a065748.jpg',
    beforeLabel: language === 'da' ? 'Under byggeri' : 'During construction',
    afterLabel: language === 'da' ? 'Efter slutrengøring' : 'After final cleaning'
  },
  {
    id: 'special',
    title: language === 'da' ? 'Specialrengøring' : 'Specialty Cleaning',
    description: language === 'da'
      ? 'Dybdegående afkalkning, fedtfjernelse og polering af sanitære og rustfrie overflader.'
      : 'In-depth descaling, degreasing, and polishing of sanitary and stainless steel surfaces.',
    before: 'https://assets.auxodev.dk/assets/b84a6f1a-652f-4145-af52-d7d46fab18e5/3f669a97-ebbc-4962-b370-e8b167045676.jpg',
    after: 'https://assets.auxodev.dk/assets/b84a6f1a-652f-4145-af52-d7d46fab18e5/a058af45-4867-41d7-b036-d89ed5ae2f9b.jpg',
    beforeLabel: language === 'da' ? 'Før dybderengøring' : 'Before deep cleaning',
    afterLabel: language === 'da' ? 'Efter polering' : 'After polishing'
  }
]

export default function BeforeAfter() {
  const { language, t } = useLanguage()
  const [activeTab, setActiveTab] = useState('erhverv')
  const [sliderPosition, setSliderPosition] = useState(50)
  const [isDragging, setIsDragging] = useState(false)
  const containerRef = useRef(null)

  const beforeAfterData = getBeforeAfterData(language)
  const activeData = beforeAfterData.find(item => item.id === activeTab)

  const handleMove = (clientX) => {
    if (!containerRef.current) return
    const rect = containerRef.current.getBoundingClientRect()
    const x = clientX - rect.left
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100))
    setSliderPosition(percentage)
  }

  const handleTouchMove = (e) => {
    if (!isDragging) return
    handleMove(e.touches[0].clientX)
  }

  const handleMouseMove = (e) => {
    if (!isDragging) return
    handleMove(e.clientX)
  }

  useEffect(() => {
    const handleMouseUp = () => setIsDragging(false)
    if (isDragging) {
      window.addEventListener('mouseup', handleMouseUp)
      window.addEventListener('touchend', handleMouseUp)
    }
    return () => {
      window.removeEventListener('mouseup', handleMouseUp)
      window.removeEventListener('touchend', handleMouseUp)
    }
  }, [isDragging])

  return (
    <section id="foer-efter" className="relative bg-gradient-to-b from-white to-[#FAF8F5] py-16 md:py-24 overflow-hidden border-t border-b border-[#E6DFD3]">
      <div className="relative z-10 mx-auto max-w-7xl px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          
          {/* Left Column: Content & Tabs */}
          <div className="flex flex-col justify-center">
            <h2 className="font-marcellus text-3xl sm:text-4xl md:text-5xl font-light tracking-[0.08em] text-[#2C302E] leading-tight uppercase mb-6">
              {language === 'da' ? (
                <>Før & efter <span className="font-normal text-[#a78458]">billeder</span></>
              ) : (
                <>Before & after <span className="font-normal text-[#a78458]">pictures</span></>
              )}
            </h2>
            <p className="text-base text-[#2C302E]/80 leading-relaxed mb-8">
              {language === 'da' 
                ? 'Se forskellen med vores professionelle rengøringsservice. Træk i skyderen på billederne til højre for at sammenligne resultatet før og efter vores grundige indsats.'
                : 'See the difference with our professional cleaning service. Drag the slider on the images to the right to compare the results before and after our thorough effort.'}
            </p>

            {/* Category Tabs */}
            <div className="flex flex-wrap gap-2 mb-8">
              {beforeAfterData.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id)
                    setSliderPosition(50)
                  }}
                  className={`px-5 py-3 text-xs font-semibold tracking-wider uppercase transition-all duration-300 rounded-none border ${
                    activeTab === tab.id
                      ? 'bg-[#a78458] border-[#a78458] text-white shadow-md'
                      : 'bg-white border-[#E6DFD3] text-[#2C302E] hover:border-[#a78458] hover:text-[#a78458]'
                  }`}
                >
                  {tab.title}
                </button>
              ))}
            </div>

            {/* Active Tab Description */}
            <div className="border-l-2 border-[#a78458] pl-4 py-1">
              <p className="text-sm text-[#2C302E]/80 leading-relaxed italic">
                {activeData.description}
              </p>
            </div>
          </div>

          {/* Right Column: Interactive Slider */}
          <div className="w-full">
            <p className="text-sm text-center text-[#2C302E]/70 mb-4 italic flex items-center justify-center gap-2">
              <ArrowLeftRight className="h-4 w-4 text-[#a78458]" />
              Træk i skyderen for at se forskellen
            </p>

            <div 
              ref={containerRef}
              className="relative w-full aspect-[4/3] sm:aspect-[16/10] md:aspect-[16/9] lg:aspect-[4/3] overflow-hidden border border-[#E6DFD3] cursor-ew-resize select-none rounded-none shadow-lg"
              onMouseDown={() => setIsDragging(true)}
              onTouchStart={() => setIsDragging(true)}
              onMouseMove={handleMouseMove}
              onTouchMove={handleTouchMove}
            >
              {/* After Image (Background - visible on the right side) */}
              <img 
                src={activeData.after} 
                alt={activeData.afterLabel}
                className="absolute inset-0 w-full h-full object-cover pointer-events-none"
              />

              {/* Before Image (Foreground, clipped - visible on the left side) */}
              <div 
                className="absolute inset-y-0 left-0 overflow-hidden pointer-events-none z-10"
                style={{ width: `${sliderPosition}%` }}
              >
                <img 
                  src={activeData.before} 
                  alt={activeData.beforeLabel}
                  className="absolute inset-y-0 left-0 w-full h-full object-cover max-w-none"
                  style={{ width: containerRef.current ? containerRef.current.offsetWidth : '100%' }}
                />
              </div>

              {/* Static Labels in corners */}
              <div className="absolute bottom-4 left-4 bg-black/70 text-white text-xs font-semibold tracking-wider uppercase px-3 py-1.5 rounded-none z-20 pointer-events-none">
                {activeData.beforeLabel}
              </div>
              <div className="absolute bottom-4 right-4 bg-[#a78458] text-white text-xs font-semibold tracking-wider uppercase px-3 py-1.5 rounded-none z-20 pointer-events-none">
                {activeData.afterLabel}
              </div>

              {/* Slider Handle Line */}
              <div 
                className="absolute inset-y-0 w-1 bg-white shadow-lg pointer-events-none z-30"
                style={{ left: `${sliderPosition}%` }}
              >
                {/* Handle Button */}
                <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-10 h-10 bg-[#a78458] border-2 border-white text-white flex items-center justify-center shadow-xl rounded-none transition-transform duration-200 hover:scale-110">
                  <ArrowLeftRight className="h-5 w-5" />
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  )
}
